import { useEffect, useState } from "react";
import { Loader2, Sparkles, Wand2, SlidersHorizontal, Bot, RefreshCw, Save, Play, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";

const SORTS = { plays: "Most played", recent: "Recently added", year: "Newest year first", year_asc: "Oldest year first", title: "Title A–Z", random: "Shuffled" };
export const DEFAULT_RULES = { genre: "", artist: "", title: "", year_min: "", year_max: "", starred: false, liked: false, include_shared: true, played_days: "", min_plays: 1, never_played: false, added_days: "", sort: "plays", limit: 100 };
const PRESETS = [
  { label: "Most played this month", name: "Most played this month", rules: { ...DEFAULT_RULES, played_days: 30, sort: "plays", limit: 50 } },
  { label: "Jazz before 1970", name: "Jazz before 1970", rules: { ...DEFAULT_RULES, genre: "jazz", year_max: 1969, sort: "year_asc" } },
  { label: "Recently added", name: "New this month", rules: { ...DEFAULT_RULES, added_days: 30, sort: "recent" } },
  { label: "Forgotten favorites", name: "Forgotten favorites", rules: { ...DEFAULT_RULES, starred: true, never_played: true, sort: "random", limit: 50 } },
  { label: "My thumbs-up", name: "My thumbs-up", rules: { ...DEFAULT_RULES, liked: true, sort: "recent", limit: 500 } },
];
const ERAS = { any: "Any era", "60s": "60s", "70s": "70s", "80s": "80s", "90s": "90s", "00s": "2000s", "10s": "2010s", "20s": "2020s" };
const AI_PRESETS = ["late-night drive", "rainy Sunday morning", "sweaty gym session", "dinner party background", "road trip singalong", "deep focus"];

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
export const toApi = (r) => ({ ...r, year_min: num(r.year_min), year_max: num(r.year_max), played_days: num(r.played_days), added_days: num(r.added_days), min_plays: Number(r.min_plays) || 1, limit: Number(r.limit) || 100 });
export const fromApi = (r) => ({ ...DEFAULT_RULES, ...r, year_min: r.year_min ?? "", year_max: r.year_max ?? "", played_days: r.played_days ?? "", added_days: r.added_days ?? "" });
export const isEmptyRules = (r) => !r.genre && !r.artist && !r.title && !r.year_min && !r.year_max && !r.starred && !r.liked && !r.played_days && !r.never_played && !r.added_days;

const Field = ({ label, children, className = "" }) => (
  <label className={`block min-w-0 text-[11px] text-zinc-500 ${className}`}>{label}<div className="mt-1 min-w-0">{children}</div></label>
);
const Num = ({ value, onChange, placeholder, testid, min = 0 }) => (
  <Input value={value} onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ""))} placeholder={placeholder} inputMode="numeric" min={min} size={4} data-testid={testid} className="h-9 w-full min-w-0 bg-white font-mono text-sm" />
);

/* Rule fields + live preview — shared by the Rules tab, the AI tab's "narrow with rules" and the Playlist Builder page. */
export const RulesForm = ({ rules, setRules, preview, compact = false }) => {
  const [genres, setGenres] = useState([]);
  const set = (k) => (v) => setRules((r) => ({ ...r, [k]: v }));
  useEffect(() => { api.get("/genres").then((r) => setGenres(r.data)).catch(() => {}); }, []);
  return (
    <>
      <div className="grid min-w-0 gap-4 sm:grid-cols-2">
        <Field label="Genre contains">
          <Input list="smart-genres" value={rules.genre} onChange={(e) => set("genre")(e.target.value)} placeholder="Any genre" data-testid="smart-genre-input" className="h-9 bg-white text-sm" />
          <datalist id="smart-genres">{genres.map((g) => <option key={g.name} value={g.name}>{g.count} songs</option>)}</datalist>
        </Field>
        <Field label="Artist contains"><Input value={rules.artist} onChange={(e) => set("artist")(e.target.value)} placeholder="Any artist" data-testid="smart-artist-input" className="h-9 bg-white text-sm" /></Field>
        <Field label="Title contains"><Input value={rules.title} onChange={(e) => set("title")(e.target.value)} placeholder="Any title" data-testid="smart-title-input" className="h-9 bg-white text-sm" /></Field>
        <div className="grid min-w-0 grid-cols-2 gap-3">
          <Field label="Year from"><Num value={rules.year_min} onChange={set("year_min")} placeholder="1900" testid="smart-year-min" /></Field>
          <Field label="Year to"><Num value={rules.year_max} onChange={set("year_max")} placeholder="2026" testid="smart-year-max" /></Field>
        </div>
        <div className="grid min-w-0 grid-cols-2 gap-3">
          <Field label="Played in last … days"><Num value={rules.played_days} onChange={set("played_days")} placeholder="any time" testid="smart-played-days" /></Field>
          <Field label="…at least N times"><Num value={rules.min_plays} onChange={set("min_plays")} placeholder="1" testid="smart-min-plays" min={1} /></Field>
        </div>
        <Field label="Added in last … days"><Num value={rules.added_days} onChange={set("added_days")} placeholder="any time" testid="smart-added-days" /></Field>
        {!compact && (
          <>
            <Field label="Order">
              <Select value={rules.sort} onValueChange={set("sort")}>
                <SelectTrigger className="h-9 bg-white text-sm" data-testid="smart-sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`smart-sort-${k}`}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Max songs"><Num value={rules.limit} onChange={set("limit")} placeholder="100" testid="smart-limit" min={1} /></Field>
          </>
        )}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-zinc-700">
        <label className="flex items-center gap-2"><Switch checked={rules.starred} onCheckedChange={set("starred")} className="scale-75" data-testid="smart-starred-switch" />Favorites only</label>
        <label className="flex items-center gap-2"><Switch checked={rules.liked} onCheckedChange={set("liked")} className="scale-75" data-testid="smart-liked-switch" />Thumbs-up songs only</label>
        <label className="flex items-center gap-2"><Switch checked={rules.never_played} onCheckedChange={set("never_played")} className="scale-75" data-testid="smart-never-switch" />Never played</label>
        <label className="flex items-center gap-2"><Switch checked={rules.include_shared} onCheckedChange={set("include_shared")} className="scale-75" data-testid="smart-shared-switch" />Include the shared library</label>
      </div>
      <p className="flex items-center gap-1.5 text-[11px] text-zinc-500" data-testid="smart-dislike-note"><ThumbsDown size={11} />Songs you gave a thumbs-down are always left out of dynamic playlists and AI mixes.</p>
      {preview !== undefined && (
        <div className="rounded-md bg-zinc-50 p-3" data-testid="smart-preview">
          <p className="text-xs text-zinc-700"><b data-testid="smart-preview-count">{preview ? preview.count : "…"}</b> song{preview?.count === 1 ? "" : "s"} match · <span className="text-zinc-500">{preview?.text}</span></p>
          {preview?.sample?.length > 0 && <p className="mt-1 truncate font-mono text-[11px] text-zinc-500">{preview.sample.map((t) => t.title).join(" · ")}{preview.count > preview.sample.length ? " · …" : ""}</p>}
        </div>
      )}
    </>
  );
};

export const useRulesPreview = (rules, enabled = true) => {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    const t = setTimeout(() => {
      api.post("/playlists/smart/preview", toApi(rules)).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 300);
    return () => clearTimeout(t);
  }, [rules, enabled]);
  return preview;
};

const Chips = ({ value, onChange, placeholder, testid }) => {
  const [text, setText] = useState("");
  const add = () => { const v = text.trim(); if (v && !value.includes(v)) onChange([...value, v]); setText(""); };
  return (
    <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1" data-testid={testid}>
      {value.map((v) => <button key={v} type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="rounded-full bg-zinc-900 px-2.5 py-0.5 text-xs text-white" data-testid={`${testid}-chip`}>{v} ×</button>)}
      <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} onBlur={add}
        placeholder={value.length ? "" : placeholder} className="min-w-[6rem] flex-1 bg-transparent text-sm outline-none" data-testid={`${testid}-input`} />
    </div>
  );
};

const AiTab = ({ rules, setRules, onSaved, onOpenChange }) => {
  const p = usePlayer();
  const [mood, setMood] = useState("");
  const [artists, setArtists] = useState([]);
  const [genres, setGenres] = useState([]);
  const [length, setLength] = useState(15);
  const [era, setEra] = useState("any");
  const [discovery, setDiscovery] = useState(20);
  const [useRules, setUseRules] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [name, setName] = useState("");
  const preview = useRulesPreview(rules, useRules);
  const spec = () => ({ mood, artists, genres, length, era, discovery, rules: useRules ? toApi(rules) : null });
  const canBuild = mood.trim() || artists.length || genres.length || (useRules && !isEmptyRules(rules));
  const build = async (retry = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/builder/generate", { ...spec(), avoid_track_ids: retry && result ? result.tracks.map((t) => t.id).slice(0, 200) : [] });
      setResult(data);
      if (!name || retry) setName(data.name || "");
      if (!data.tracks.length) toast("No songs matched — loosen the rules or add a mood.");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/playlists", { name: name.trim() || result.name, description: result.description || "", track_ids: result.tracks.map((t) => t.id), source: "ai", ai_spec: spec() });
      toast.success(`Created "${data.name}"`, { description: "A dynamic AI playlist — hit “Refresh with AI” any time for a fresh take on the same recipe." });
      onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  return (
    <>
      <Field label="Describe the vibe">
        <Input value={mood} onChange={(e) => setMood(e.target.value)} placeholder="late-night drive, rainy sunday, sweaty gym…" data-testid="ai-mood-input" className="h-10 bg-white" />
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ai-presets">
          {AI_PRESETS.map((m) => <button key={m} type="button" onClick={() => setMood(m)} className="rounded-full border border-zinc-200 bg-white px-2.5 py-0.5 text-[11px] text-zinc-700 hover:border-zinc-900" data-testid={`ai-preset-${m.replace(/\s+/g, "-")}`}>{m}</button>)}
        </div>
      </Field>
      <div className="grid min-w-0 gap-4 sm:grid-cols-2">
        <Field label="Artists to lean on"><Chips value={artists} onChange={setArtists} placeholder="Type a name, press Enter" testid="ai-artists" /></Field>
        <Field label="Genres"><Chips value={genres} onChange={setGenres} placeholder="jazz, soul, indie…" testid="ai-genres" /></Field>
        <Field label={`Length · ${length} songs`}><Slider value={[length]} onValueChange={([v]) => setLength(v)} min={5} max={60} step={1} data-testid="ai-length-slider" className="mt-3" /></Field>
        <Field label="Era">
          <Select value={era} onValueChange={setEra}>
            <SelectTrigger className="h-9 bg-white text-sm" data-testid="ai-era-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(ERAS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={`Discoveries · ${discovery === 0 ? "only my library" : discovery < 35 ? "a few new finds" : discovery < 70 ? "an even blend" : "mostly new"}`} className="sm:col-span-2">
          <Slider value={[discovery]} onValueChange={([v]) => setDiscovery(v)} min={0} max={100} step={5} data-testid="ai-discovery-slider" className="mt-3" />
        </Field>
      </div>
      <div className="rounded-md border border-zinc-200 p-3" data-testid="ai-rules-box">
        <label className="flex items-center justify-between gap-3 text-sm">
          <span className="flex items-center gap-2"><SlidersHorizontal size={14} className="text-amber-600" />Narrow the pool with rules first<span className="text-xs text-zinc-500">— AI only picks from songs that match</span></span>
          <Switch checked={useRules} onCheckedChange={setUseRules} data-testid="ai-use-rules-switch" />
        </label>
        {useRules && <div className="mt-4 space-y-4"><RulesForm rules={rules} setRules={setRules} preview={preview} compact /></div>}
      </div>
      {result && (
        <div className="rounded-md bg-zinc-50 p-4" data-testid="ai-result">
          <Field label="Playlist name"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="ai-name-input" className="h-9 bg-white" /></Field>
          {result.description && <p className="mt-2 text-xs text-zinc-600" data-testid="ai-result-description">{result.description}</p>}
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500"><span data-testid="ai-result-count">{result.tracks.length}</span> songs from your library</p>
          <ol className="mt-1 max-h-40 overflow-y-auto text-sm" data-testid="ai-result-tracks">
            {result.tracks.map((t, i) => <li key={t.id} className="flex gap-3 border-b border-zinc-200/70 py-1 last:border-0"><span className="w-5 font-mono text-xs text-zinc-400">{i + 1}</span><span className="truncate">{t.title}</span><span className="truncate text-zinc-500">{t.artist}</span></li>)}
          </ol>
          {result.discoveries?.length > 0 && <p className="mt-2 text-[11px] text-zinc-500" data-testid="ai-result-discoveries">+ {result.discoveries.length} discoveries not in your library (see them in the Playlist Builder)</p>}
        </div>
      )}
      <DialogFooter className="gap-2 sm:justify-between">
        <div className="flex gap-2">
          {result && <button onClick={() => build(true)} disabled={busy} data-testid="ai-retry-button" className="flex items-center gap-2 rounded-full border border-zinc-300 px-4 py-2 text-sm hover:border-zinc-900 disabled:opacity-40"><RefreshCw size={14} className={busy ? "animate-spin" : ""} />Try again</button>}
          {result?.tracks.length > 0 && <button onClick={() => p.playTracks(result.tracks)} data-testid="ai-play-button" className="flex items-center gap-2 rounded-full border border-zinc-300 px-4 py-2 text-sm hover:border-zinc-900"><Play size={14} />Play</button>}
        </div>
        {result
          ? <button onClick={save} disabled={saving || !result.tracks.length} data-testid="ai-save-button" className="flex items-center gap-2 rounded-full bg-zinc-900 px-6 py-2 text-sm text-white disabled:opacity-40">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}Save dynamic playlist</button>
          : <button onClick={() => build(false)} disabled={busy || !canBuild} data-testid="ai-build-button" className="flex items-center gap-2 rounded-full bg-zinc-900 px-6 py-2 text-sm text-white disabled:opacity-40">{busy ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}{busy ? "Picking songs…" : "Build with AI"}</button>}
      </DialogFooter>
    </>
  );
};

const RulesTab = ({ rules, setRules, initial, onSaved, onOpenChange }) => {
  const [name, setName] = useState(initial?.name || "");
  const [busy, setBusy] = useState(false);
  const preview = useRulesPreview(rules);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = initial
        ? await api.put(`/playlists/${initial.id}/rules`, toApi(rules))
        : await api.post("/playlists/smart", { name: name.trim(), description: "", rules: toApi(rules) });
      toast.success(initial ? "Rules updated" : `Created "${data.name}"`, { description: `${data.track_count ?? data.tracks?.length ?? 0} songs match right now — it refreshes itself.` });
      onSaved(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <>
      {!initial && (
        <div className="flex flex-wrap gap-2" data-testid="smart-presets">
          {PRESETS.map((pr) => (
            <button key={pr.label} type="button" onClick={() => { setRules(pr.rules); if (!name) setName(pr.name); }} data-testid={`smart-preset-${pr.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs text-zinc-700 hover:border-zinc-900"><Wand2 size={11} />{pr.label}</button>
          ))}
        </div>
      )}
      {!initial && <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jazz before 1970" data-testid="smart-name-input" className="h-10 bg-white" /></Field>}
      <RulesForm rules={rules} setRules={setRules} preview={preview} />
      <DialogFooter>
        <button onClick={save} disabled={busy || (!initial && !name.trim())} data-testid="smart-save-button" className="flex items-center gap-2 rounded-full bg-zinc-900 px-6 py-2 text-sm text-white disabled:opacity-40">
          {busy && <Loader2 size={14} className="animate-spin" />}{initial ? "Save rules" : "Create rules playlist"}
        </button>
      </DialogFooter>
    </>
  );
};

const TABS = [
  { id: "rules", label: "Rules", icon: SlidersHorizontal, hint: "Self-updating — songs are picked by filters every time you open it." },
  { id: "ai", label: "AI", icon: Bot, hint: "Describe a vibe (optionally fenced by rules) and AI curates a list you can refresh any time." },
];

export const DynamicPlaylistDialog = ({ open, onOpenChange, initial = null, onSaved, defaultTab = "rules" }) => {
  const [tab, setTab] = useState(defaultTab);
  const [rules, setRules] = useState(DEFAULT_RULES);
  useEffect(() => {
    if (!open) return;
    setTab(initial ? "rules" : defaultTab);
    setRules(initial?.rules ? fromApi(initial.rules) : DEFAULT_RULES);
  }, [open, initial, defaultTab]);
  const current = TABS.find((t) => t.id === tab) || TABS[0];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-2rem)] max-w-2xl overflow-x-hidden overflow-y-auto" data-testid="smart-playlist-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading text-2xl"><Sparkles size={18} className="text-amber-600" />{initial ? "Edit rules" : "New dynamic playlist"}</DialogTitle></DialogHeader>
        {!initial && (
          <div className="flex w-fit max-w-full gap-1 rounded-full border border-zinc-200 bg-zinc-50 p-1" data-testid="dynamic-tabs">
            {TABS.map((t) => (
              <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`dynamic-tab-${t.id}`} aria-selected={tab === t.id}
                className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm transition-colors ${tab === t.id ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}><t.icon size={14} />{t.label}</button>
            ))}
          </div>
        )}
        <p className="-mt-1 text-xs text-zinc-500" data-testid="dynamic-tab-hint">{current.hint}</p>
        {tab === "ai" && !initial
          ? <AiTab rules={rules} setRules={setRules} onSaved={onSaved} onOpenChange={onOpenChange} />
          : <RulesTab rules={rules} setRules={setRules} initial={initial} onSaved={onSaved} onOpenChange={onOpenChange} />}
      </DialogContent>
    </Dialog>
  );
};

export const SmartPlaylistDialog = DynamicPlaylistDialog;
